import { ChassisCrafting } from '@components/arsenal/forge/ChassisCrafting';
import { WeaponCrafting } from '@components/arsenal/forge/WeaponCrafting';
import { ItemCrafting } from '@components/arsenal/forge/ItemCrafting';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/Button';
import { Modal } from '@components/ui/Modal';
import { usePurchaseMutation } from '@http';
import { toast } from 'react-toastify';
import { useAppStore } from '@stores';
import * as Shared from '@shared/lib';
import { useState } from 'react';

interface MarketListingProps {
  value: string;
  type: 'CHASSIS' | 'WEAPON' | 'ITEM';
};

export function MarketListing({ value, type }: MarketListingProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isBuying, setIsBuying] = useState(false);

  const { t } = useTranslation(undefined, { keyPrefix: 'Market' });
  const { mutateAsync } = usePurchaseMutation();
  const { userData, refetch } = useAppStore();

  const data = {
    CHASSIS: Shared.chassisData,
    WEAPON: Shared.weaponsData,
    ITEM: Shared.itemData,
  }[type].find((c) => c.key === value);
  if (!data) return null;

  const crafting = data.crafting;
  if (!crafting) return null;

  const canBuy = userData.credits >= crafting.credits;

  const tryBuy = () => {
    if (!canBuy) return;
    setShowConfirm(true);
  };

  const close = () => setShowConfirm(false);
  const confirm = async () => {
    setIsBuying(true);
  
    await mutateAsync({
      key: value,
      type,
    });
  
    setIsBuying(false);
    setShowConfirm(false);

    refetch();

    toast(t('thankYouForPurchase'), { type: 'success' });
  };

  return (
    <>
      <Modal onRequestClose={() => setShowConfirm(false)} title={t('confirmPurchase')} isOpen={showConfirm}>
        <p className="text-center my-4 text-sm px-4">
          {t('confirmPurchaseMessage', { credits: crafting.credits })}
        </p>
        <div className="flex justify-center mt-4">
          <Button className="border-l-0 border-b-0" isLoading={isBuying} onClick={close}>
            {t('cancel')}
          </Button>
          <Button className="border-x-0 border-b-0" isLoading={isBuying} onClick={confirm}>
            {t('buy')}
          </Button>
        </div>
      </Modal>

      <div className={`${canBuy && 'border-primary border-b'} market-listing`}>
        {type === 'CHASSIS' && <ChassisCrafting chassis={value} />}
        {type === 'WEAPON' && <WeaponCrafting weapon={value} />}
        {type === 'ITEM' && <ItemCrafting item={value} />}

        <button className={`${canBuy && 'can-buy'} market-listing-btn`} onClick={tryBuy}>
          <p className={`${!canBuy && 'text-gray-500'} text-sm`}>
            {t(canBuy ? 'buyBlueprint' : 'cantAfford')}
          </p>

          <div className="flex items-center">
            <p className={`${canBuy ? 'text-black' : 'text-red-500'} text-sm text-mono`}>
              {crafting.credits}
            </p>
            <img src="/assets/items/credit.avif" className="h-4.5 ml-1" alt="credit" />
          </div>
        </button>
      </div>
    </>
  );
}